/**
 * An auction's prize split - the Manager Hold plus one percentage per winning place - as the
 * Create Auction page (Super-admin/CreateAuction.js) and the Update Auction modal
 * (Super-admin/Auction.js) enter it, kept pure so it can be tested on its own.
 *
 * usp_CRUDAuction accepts an auction only when the Manager Hold (xcuttaTake) and the winner
 * percentages add up to exactly 100: each is a share of the whole pool, which is what the prize
 * procedures pay out of. Neither form ever showed what had been entered (#146). Create had no
 * working check at all and left the refusal to the server, whose message named no figure; Update
 * compared a floating-point sum with !== 100, so 5 + 33.7 + 30.6 + 30.7 came to
 * 100.00000000000001 and an exact split was refused.
 *
 * Everything here works in whole hundredths read from the typed digits - the precision the
 * database stores, DECIMAL(5, 2) - so no floating-point value ever takes part in a comparison.
 * The bounds match the procedure's: 0 <= Manager Hold < 100 (a hold of 100 leaves nothing for the
 * winners), 0 <= each place <= 100, and the sum exactly 100. More than two decimals is refused here
 * rather than rounded by the server without telling anyone.
 */

/** 100%, in hundredths. */
export const FULL_SPLIT = 10000;

// Up to three whole digits and two decimals. A number input reports ".5" and "5." as typed, so
// both are accepted; a sign, an exponent or a third decimal is not.
const PERCENT_PATTERN = /^(\d{1,3}(\.\d{0,2})?|\.\d{1,2})$/;

/**
 * Reads one percentage field.
 *
 * @param {*} value What the field holds: a string as typed, or a number from the API.
 * @returns {{state: 'empty'|'valid'|'invalid', hundredths: number|null}} hundredths is set only
 *   when the value is valid.
 */
export const parsePercent = (value) => {
    const text = String(value ?? '').trim();
    if (text === '') return { state: 'empty', hundredths: null };
    if (!PERCENT_PATTERN.test(text)) return { state: 'invalid', hundredths: null };

    const [whole, fraction = ''] = text.split('.');
    return { state: 'valid', hundredths: Number(whole || '0') * 100 + Number(fraction.padEnd(2, '0')) };
};

/** A field read by parsePercent, refused when it is above max (in hundredths). */
const readPart = (value, max) => {
    const part = parsePercent(value);
    return part.state === 'valid' && part.hundredths > max ? { state: 'invalid', hundredths: null } : part;
};

/** What a part contributes to the total: an empty or invalid one counts as nothing. */
const counted = (part) => (part.state === 'valid' ? part.hundredths : 0);

/**
 * Formats hundredths as a percentage figure, without trailing zeros: 9250 is "92.5", 10000 "100".
 * Built from the integer, so it never shows a floating-point tail.
 *
 * @param {number} hundredths The value in hundredths.
 * @returns {string} The figure, without the % sign.
 */
export const formatPercent = (hundredths) => {
    const value = Math.round(Number(hundredths) || 0);
    const magnitude = Math.abs(value);
    const fraction = String(magnitude % 100).padStart(2, '0').replace(/0+$/, '');
    return `${value < 0 ? '-' : ''}${Math.floor(magnitude / 100)}${fraction ? `.${fraction}` : ''}`;
};

/**
 * Reads a whole prize split.
 *
 * @param {*} managerHold The Manager Hold field.
 * @param {Array} winnerPercentages One field per winning place, in place order.
 * @returns {{managerHold: Object, winners: Object[], winnersTotal: number, total: number,
 *   remaining: number, status: 'under'|'exact'|'over'}} Parts as parsePercent reads them (a hold
 *   of 100 or more, or a place above 100, reads as invalid); totals in hundredths, with empty and
 *   invalid parts counted as nothing, so the total means something before every field is filled.
 */
export const summarizePrizeSplit = (managerHold, winnerPercentages) => {
    const hold = readPart(managerHold, FULL_SPLIT - 1);
    const winners = (Array.isArray(winnerPercentages) ? winnerPercentages : []).map((value) =>
        readPart(value, FULL_SPLIT)
    );
    const winnersTotal = winners.reduce((sum, part) => sum + counted(part), 0);
    const total = counted(hold) + winnersTotal;

    return {
        managerHold: hold,
        winners,
        winnersTotal,
        total,
        remaining: FULL_SPLIT - total,
        status: total === FULL_SPLIT ? 'exact' : total > FULL_SPLIT ? 'over' : 'under',
    };
};

/**
 * The text of the running total shown under the winner percentages.
 *
 * @param {Object} summary From summarizePrizeSplit.
 * @returns {{winnersTarget: string, allocated: string, balance: string}} winnersTarget is what the
 *   places must share (100 minus a valid hold; "-" while the hold is invalid); allocated is the
 *   running total; balance is what is left, or by how much the split is over.
 */
export const describePrizeSplit = (summary) => {
    const hold = summary.managerHold;
    const balance =
        summary.status === 'exact'
            ? 'Fully allocated'
            : summary.status === 'over'
                ? `Over by ${formatPercent(-summary.remaining)}%`
                : `Remaining: ${formatPercent(summary.remaining)}%`;

    return {
        winnersTarget: hold.state === 'invalid' ? '-' : formatPercent(FULL_SPLIT - counted(hold)),
        allocated:
            `Allocated: ${formatPercent(summary.total)}% of 100% ` +
            `(Manager Hold ${formatPercent(counted(hold))}% + winners ${formatPercent(summary.winnersTotal)}%)`,
        balance,
    };
};

const HOLD_RULE = 'The Manager Hold must be at least 0% and less than 100%, with at most two decimal places.';

const placeName = (placeLabels, index) => String(placeLabels?.[index] ?? '').trim() || `Place ${index + 1}`;

const holdProblem = (hold, reportEmpty) => {
    if (hold.state === 'invalid') return HOLD_RULE;
    if (hold.state === 'empty' && reportEmpty) return 'Please enter the Manager Hold.';
    return null;
};

const placeProblem = (part, name, reportEmpty) => {
    if (part.state === 'invalid') return `${name} must be between 0% and 100%, with at most two decimal places.`;
    if (part.state === 'empty' && reportEmpty) return `Please enter a percentage for ${name}.`;
    return null;
};

/**
 * The first field that holds something unusable, ignoring fields that are still empty - what the
 * running total flags while the manager is typing.
 *
 * @param {Object} summary From summarizePrizeSplit.
 * @param {string[]} [placeLabels] The places' titles, in place order; "Place N" when missing.
 * @returns {string|null} The sentence to show, or null.
 */
export const prizeSplitInputProblem = (summary, placeLabels) => {
    const hold = holdProblem(summary.managerHold, false);
    if (hold) return hold;

    for (let index = 0; index < summary.winners.length; index += 1) {
        const place = placeProblem(summary.winners[index], placeName(placeLabels, index), false);
        if (place) return place;
    }
    return null;
};

/**
 * Why the split cannot be saved, naming the total and the gap - or null when it can. The sum
 * sentences are the ones usp_CRUDAuction returns, so a refusal reads the same from either side.
 *
 * @param {Object} summary From summarizePrizeSplit.
 * @param {string[]} [placeLabels] The places' titles, in place order; "Place N" when missing.
 * @returns {string|null} The first problem, or null.
 */
export const prizeSplitProblem = (summary, placeLabels) => {
    const hold = holdProblem(summary.managerHold, true);
    if (hold) return hold;

    if (summary.winners.length === 0) return 'Please select the number of winners.';

    for (let index = 0; index < summary.winners.length; index += 1) {
        const place = placeProblem(summary.winners[index], placeName(placeLabels, index), true);
        if (place) return place;
    }

    const total = formatPercent(summary.total);
    if (summary.status === 'over') {
        return `The Manager Hold and winner percentages add up to ${total}%. ` +
            `Reduce them by ${formatPercent(-summary.remaining)}% so they total exactly 100%.`;
    }
    if (summary.status === 'under') {
        return `The Manager Hold and winner percentages add up to ${total}%. ` +
            `${formatPercent(summary.remaining)}% is still unallocated; add it so they total exactly 100%.`;
    }
    return null;
};

/**
 * The validated values to send, as numbers: what was checked is what is saved ("010" goes out as
 * 10). Only meaningful once prizeSplitProblem has returned null.
 *
 * @param {Object} summary From summarizePrizeSplit.
 * @returns {{xcuttaTake: number, winnerPercentage: number[]}} hundredths / 100, which serialises
 *   as the two-decimal value typed (3370 / 100 is 33.7).
 */
export const prizeSplitPayload = (summary) => ({
    xcuttaTake: counted(summary.managerHold) / 100,
    winnerPercentage: summary.winners.map((part) => counted(part) / 100),
});
